import React, { Fragment } from "react";
// import PropTypes from "prop-types";
import clsx from "clsx";
import moment from "moment";
import { Button, Col, Container, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AccessControl,
  ButtonManageCourse,
  CoursePreviewCard,
  NoData,
  TabContent,
} from "@components";
import { useCourseInfo } from "./hooks";
import styles from "./style/CourseDetailViewPage.module.css";
import { addCourseToCartAction } from "@redux/actions";
import { Avatar } from "@components/common";
import {
  ADMIN_ROLE,
  COURSE_STATUS,
  DATE_FORMAT,
  GUEST_ROLE,
  INSTRUCTOR_ROLE,
  STUDENT_ROLE,
} from "@constants";
import { useDispatch } from "react-redux";
import { getDiscountValid } from "@helpers";

const CourseInfoPlaceholder = () => {
  return (
    <Row className="gy-4">
      <h3 className="placeholder col-11 mb-0"></h3>
      <span className="placeholder col-9"></span>
      <Col sm={12} className="p-0">
        <Button
          className="px-5 placeholder"
          variant="warning"
          disabled
        ></Button>
      </Col>
      <Col sm={12} className="p-0">
        <span className="placeholder col-4"></span>
      </Col>
      <span className="placeholder col-7"></span>
      <span className="placeholder col-9"></span>
      <Col sm={12} className="p-0">
        <Button className="px-5 placeholder" disabled></Button>
        <Button
          variant="outline-light"
          className="px-5 placeholder ml-2"
        ></Button>
      </Col>
    </Row>
  );
};

const CourseDetailViewPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: courseId } = useParams();
  const {
    processing,
    courseDetails,
    handleSaveCourse,
    handleEnroll,
    isEnrolled,
    checkEnrollProcessing,
    enrollProcessing,
    instructorInfo,
    handleLearn,
    tabInfoList,
    savingProcessing,
    handleSubscribe,
    handleUnsubscribe,
    handleChangeStatus,
    subscribe,
    unsubscribe,
    isSubscribed,
    defaultTab,
    tabRef,
    isSaved,
    processingCheck,
    isAuth,
    isOwner,
    isAdmin,
  } = useCourseInfo(courseId);

  if (!processing && !courseDetails) {
    return (
      <NoData
        className={styles.noDataContainer}
        title="This course was not found!"
        message={
          <Link className="text-danger" to="/explore">
            Explore Courses
          </Link>
        }
      />
    );
  }

  const {
    updatedAt,
    createdAt,
    thumbnailUrl,
    previewVideoUrl,
    isBestSeller,
    title,
    shortDescription,
    avgRate,
    numRatings,
    totalEnrolls,
    audioLanguages,
    isPurchased,
    price,
    // isEnroll,
    isFree,
    id,
    status,
    levels,
    discounts,
  } = courseDetails || {};

  // logic
  const handleEditCourse = () => {
    navigate(`/update-course/${courseId}`, {
      state: { isUpdate: true },
    });
  };

  // UI
  let subscribeBtn = null;
  if (instructorInfo) {
    subscribeBtn = !isAuth ? (
      <Button onClick={() => handleSubscribe()}>Subscribe</Button>
    ) : isSubscribed.data ? (
      <Button
        variant="outline-primary"
        onClick={() => handleUnsubscribe(instructorInfo.id)}
        disabled={isSubscribed.processing || unsubscribe.processing}
      >
        {isSubscribed.processing || unsubscribe.processing
          ? "Loading..."
          : "Subscribed"}
      </Button>
    ) : (
      <Button
        onClick={() => handleSubscribe(instructorInfo.id)}
        disabled={isSubscribed.processing || subscribe.processing}
      >
        {isSubscribed.processing || unsubscribe.processing
          ? "Loading..."
          : "Subscribe"}
      </Button>
    );
  }

  const lastUpdatedDate = moment(updatedAt || createdAt || null).format(
    DATE_FORMAT
  );

  const renderInstructorInfo = (
    <Row>
      <Col xs={12} sm={6} className="d-flex mb-4">
        <Avatar
          src={instructorInfo?.avatarUrl}
          width={65}
          isProcessing={processing}
        />
        <div className="d-flex flex-column ml-2">
          <Link
            className={clsx(
              "h6 text-decoration-none text-black",
              processing && "placeholder"
            )}
            to={`/instructor-profile-view/${instructorInfo?.id}`}
          >
            {instructorInfo?.fullName || "Unknown author"}
          </Link>
          {processing ? (
            <Button className="placeholder px-5" disabled></Button>
          ) : !instructorInfo?.isSubscription ? (
            subscribeBtn
          ) : null}
        </div>
      </Col>
    </Row>
  );

  const buttonSave = (
    <AccessControl targetRoles={[STUDENT_ROLE, INSTRUCTOR_ROLE]}>
      <button
        className={clsx("mr-4 h5", styles.buttonSaveCourse)}
        onClick={handleSaveCourse}
        disabled={savingProcessing || processingCheck}
      >
        {isSaved ? (
          <>
            <i className="bi bi-heart-fill"></i>
          </>
        ) : (
          <>
            <i className="bi bi-heart"></i>
          </>
        )}
      </button>
    </AccessControl>
  );

  const buttonForCourseFree = isEnrolled ? (
    <Button
      onClick={handleLearn}
      disabled={checkEnrollProcessing || enrollProcessing}
    >
      Learn
    </Button>
  ) : enrollProcessing ? (
    <Button disabled>
      <Spinner
        as="span"
        animation="border"
        size="sm"
        role="status"
        aria-hidden="true"
        className="mr-2"
      />
      Enrolling
    </Button>
  ) : (
    <Button
      onClick={handleEnroll}
      disabled={checkEnrollProcessing || enrollProcessing}
    >
      Enroll
    </Button>
  );

  const buttonForCourse =
    !isPurchased && !isEnrolled ? (
      <Fragment>
        <Button
          className="mr-2"
          onClick={() =>
            dispatch(
              addCourseToCartAction({
                courseId: id,
              })
            )
          }
        >
          Add to Cart
        </Button>
        <Button
          variant="outline-light"
          className="mr-2"
          onClick={async () => {
            await dispatch(
              addCourseToCartAction({
                courseId: id,
              })
            );
            navigate("/shopping-cart", {
              state: { courseId: id },
            });
          }}
        >
          Buy Now
        </Button>
      </Fragment>
    ) : (
      buttonForCourseFree
    );

  const ownerButton = () => {
    if (status === COURSE_STATUS.PENDING)
      return (
        <Button className="mr-2" variant="warning">
          Pending
        </Button>
      );
    if (status === COURSE_STATUS.BLOCK)
      return <Button className="mr-2">Blocked</Button>;
    if (status === COURSE_STATUS.REJECT)
      return <Button className="mr-2">Rejected</Button>;
    if (status === COURSE_STATUS.APPROVE)
      return (
        <div>
          <Button className="mr-2" variant="success">
            Published
          </Button>
          <Button onClick={handleEditCourse} className="mr-2">
            Edit
          </Button>
        </div>
      );
    return null;
  };

  const adminButton = (
    <ButtonManageCourse
      courseId={courseId}
      status={status}
      onChangeStatus={handleChangeStatus}
    />
  );

  const mainButtonGroup = (
    <Fragment>
      <p className={styles.price}>
        {isFree ? (
          "FREE"
        ) : discounts && getDiscountValid(discounts) !== 0 ? (
          <div className={clsx(styles.discountAppear)}>
            <span>
              {`$${(price * (1 - getDiscountValid(discounts))).toFixed(2)}`}
            </span>
            <span className={clsx(styles.oldPrice)}>{price}</span>
          </div>
        ) : (
          <span> {`$${price}`}</span>
        )}
      </p>

      <AccessControl targetRoles={[GUEST_ROLE]}>
        {price === 0 ? (
          <Button onClick={() => navigate("/sign-in")}>Enroll</Button>
        ) : (
          <Fragment>
            <Button className="mr-2" onClick={() => navigate("/sign-in")}>
              Add to Cart
            </Button>
            <Button
              variant="outline-light"
              className="mr-2"
              onClick={() => navigate("/sign-in")}
            >
              Buy Now
            </Button>
          </Fragment>
        )}
      </AccessControl>

      <AccessControl targetRoles={[STUDENT_ROLE, INSTRUCTOR_ROLE]}>
        {isOwner
          ? ownerButton()
          : isFree
          ? buttonForCourseFree
          : buttonForCourse}
      </AccessControl>

      <AccessControl targetRoles={[ADMIN_ROLE]}>
        {isAdmin && adminButton}
      </AccessControl>
    </Fragment>
  );

  return (
    <div className="placeholder-glow">
      {/* Course info */}
      <div className={styles.infoContainer}>
        <Container>
          <Row>
            <Col xl={4} lg={5} md={6}>
              <CoursePreviewCard
                imgPreview={thumbnailUrl}
                videoPreview={previewVideoUrl}
                isBestSeller={isBestSeller}
                isProcessing={processing}
              />
              <div className="my-3">{!isOwner && buttonSave}</div>
            </Col>
            <Col xl={8} lg={7} md={6} className={styles.rightContent}>
              {processing ? (
                <CourseInfoPlaceholder />
              ) : (
                <>
                  <h3 className="m-0">{title}</h3>
                  <p className="m-0">{shortDescription}</p>
                  {avgRate !== 0 && (
                    <div className="d-flex">
                      <div className={clsx(styles.rate, "mr-2")}>
                        <i className="bi bi-star mr-2"></i>
                        {avgRate
                          ? Number.isInteger(avgRate)
                            ? avgRate
                            : avgRate.toFixed(1)
                          : 0}
                      </div>
                      {numRatings && `(${numRatings} ratings)`}
                      {totalEnrolls && (
                        <div>
                          {totalEnrolls || 0}{" "}
                          {totalEnrolls > 1 ? "students" : "student"} enrolled
                        </div>
                      )}
                    </div>
                  )}
                  <div className="d-flex">
                    {levels?.length !== 0 && (
                      <div className="mr-3">
                        <span>
                          <i className="bi bi-reception-4 mr-2"></i>
                        </span>
                        {levels?.map((lev) => lev.name).join(", ")}
                      </div>
                    )}
                    {audioLanguages?.length !== 0 && (
                      <div className="mr-3">
                        <span>
                          <i className="bi bi-chat mr-2"></i>
                        </span>
                        {audioLanguages[0].name}
                      </div>
                    )}
                  </div>
                  <div>Last updated {lastUpdatedDate}</div>
                  <div>{mainButtonGroup}</div>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      <TabContent
        ref={tabRef}
        data={tabInfoList}
        navClassName={styles.userDetailContainer}
        topContent={!isOwner && renderInstructorInfo}
        defaultTabIndex={defaultTab}
      />
    </div>
  );
};

/* required: display name */
CourseDetailViewPage.displayName = "CourseDetailViewPage";

/* required: props types */
CourseDetailViewPage.propTypes = {};

/* required: default props */
CourseDetailViewPage.defaultProps = {};

export { CourseDetailViewPage };
